import { useState, useEffect } from 'react'
import { ClientConfig } from '../config/clients'
import { subscribeComments, postComment, Comment } from '../services/CommentsService'
import { isAccountRequiredError } from '../config/firebase'

interface Props {
  client: ClientConfig
  onClose: () => void
  onScan: () => void
}

export default function ExperienceDetail({ client, onClose, onScan }: Props) {
  const [activeTab, setActiveTab] = useState<'about' | 'apparel' | 'comments'>('about')
  const [comments, setComments] = useState<Comment[]>([])
  const [commentText, setCommentText] = useState('')

  const experienceItem = (client).experienceItem
  const experienceConfig = (client as any).experienceConfig
  
  // Dynamic fallbacks pulling from types setup
  const title = experienceItem?.title || client.clientName
  const subtitle = experienceItem?.subtitle || client.clientName
  const thumbnail = experienceItem?.thumbnail || '/rockarlogo.png'
  const animationId = client.clientId

  const innerAboutImage = experienceItem?.aboutInnerImage
  const innerAboutOverlay = experienceItem?.aboutImageOverlay
  const mainDescriptionText = experienceItem?.description || ''
  const collabText = experienceItem?.collaborationText || ''
  
  const themeColor = experienceItem?.themeAccent || '#E91E8C'
  const ctaUrl = experienceItem?.ctaUrl || 'https://rockablock.com'
  const ctaText = experienceItem?.ctaText || 'Check out the website'

  // apparel items stuff?
  const apparelItems = experienceItem?.apparel || []

  // featured badge
  const isFeatured = experienceItem?.isFeatured || false
  

  // Dynamic statistics fallback
  const currentStats = experienceItem?.stats || {
    scans: '0',
    activeUsers: '0',
    engagementRate: '0%'
  }

  useEffect(() => {
    const unsub = subscribeComments(animationId, setComments)
    return unsub
  }, [animationId])

  const handlePostComment = async () => {
    const text = commentText.trim()
    if (!text) return
    try {
      await postComment(animationId, text)
      setCommentText('')
    } catch (err) {
      if (isAccountRequiredError(err)) {
        window.alert('You need to sign up or log in before commenting.')
      }
    }
  }

  return (
    <div className="exp-detail-overlay">
      {/* Hero Header Layer (Kept identical to your setup) */}
      <div className="exp-detail-hero">
        <img src={thumbnail} alt={title} className="exp-detail-hero-img" />
        <div className="exp-detail-hero-gradient" />
        <button className="exp-detail-back" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="exp-detail-hero-badges">
          {isFeatured && <span className="exp-featured-badge">Featured</span>}
          <div className="exp-partner-logos">
            <span className="exp-partner-tag">{client.clientName}</span>
            <span className="exp-partner-sep">|</span>
            <img src="/rockarlogo.png" alt="ROCK AR" className="exp-partner-logo" />
          </div>
        </div>
      </div>

      <div className="exp-detail-body">
        {/* Title Row - Matches layout with the external link inline token icon */}
        <div className="exp-detail-title-row" style={{ alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h2 className="exp-detail-title" style={{ fontSize: '24px', fontWeight: '800', lineHeight: '1.2' }}>{title}</h2>
            <p className="exp-detail-subtitle" style={{ marginTop: '12px', color: '#aaa', fontSize: '14px', lineHeight: '1.4' }}>{subtitle}</p>
          </div>
          <a href={ctaUrl} target="_blank" rel="noopener noreferrer" className="exp-action-btn" style={{ marginTop: '4px', color: '#fff' }} aria-label="External Link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </a>
        </div>

        {/* Scan Call to Action Pill Button matches design */}
        <button className="exp-scan-cta" onClick={onScan} style={{ backgroundColor: themeColor, fontWeight: '600', borderRadius: '25px', padding: '14px' }}>
          Scan
        </button>

        {/* Navigation Tab Headers System */}
        <div className="exp-tabs">
          <button className={`exp-tab ${activeTab === 'about' ? 'active' : ''}`} onClick={() => setActiveTab('about')}>
            About
          </button>
          <button className={`exp-tab ${activeTab === 'apparel' ? 'active' : ''}`} onClick={() => setActiveTab('apparel')}>
            Apparel/Pins
          </button>
          <button className={`exp-tab ${activeTab === 'comments' ? 'active' : ''}`} onClick={() => setActiveTab('comments')}>
            Comments ({comments.length})
          </button>
        </div>

        {/* TAB VIEWS CONTROLLER */}
        
        {/* About Tab View Setup */}
        {activeTab === 'about' && (
          <div className="exp-tab-content">
            {/* The Inner Experience Featured Block Asset */}
            {innerAboutImage && (
              <div className="exp-inner-feature-card" style={{ position: 'relative', width: '100%', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' }}>
                <img src={innerAboutImage} alt="Experience Showcase" style={{ width: '100%', display: 'block' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.85))', padding: '16px' }}>
                  <p style={{ color: '#fff', fontSize: '13px', margin: 0, fontWeight: '500', lineHeight: '1.4' }}>{innerAboutOverlay}</p>
                </div>
              </div>
            )}
            
            {/* Main Description Paragraph text */}
            <p className="exp-about-text" style={{ color: '#ccc', lineHeight: '1.5' }}>{mainDescriptionText}</p>
            
            {/* Secondary custom block if alternative texts exist */}
            {collabText && (
              <div className="exp-collab-section" style={{ marginTop: '20px' }}>
                <h5 className="exp-collab-title">About the Collaboration</h5>
                <p className="exp-about-text exp-collab-desc">{collabText}</p>
              </div>
            )}

            {/* Dynamic Statistics Block Bar - Reads straight from experienceItem.stats data 
            <div className="exp-stats-bar" style={{ marginTop: '24px' }}>
              <div className="exp-stat-item">
                <p className="exp-stat-value">{currentStats.scans}</p>
                <p className="exp-stat-label">Total Scans</p>
              </div>
              <div className="exp-stat-divider" />
              <div className="exp-stat-item">
                <p className="exp-stat-value">{currentStats.activeUsers}</p>
                <p className="exp-stat-label">Active Users</p>
              </div>
              <div className="exp-stat-divider" />
              <div className="exp-stat-item">
                <p className="exp-stat-value">{currentStats.engagementRate}</p>
                <p className="exp-stat-label">Engagement</p>
              </div>
            </div>
             */}
          </div>
    
        )}

        {/* Apparel Tab View Setup */}
        {activeTab === 'apparel' && (
          <div className="exp-tab-content">
            {apparelItems.length === 0 ? (
              <p className="exp-comments-empty">No apparel or pins added yet.</p>
            ) : (
              <div className="exp-apparel-grid">
                {apparelItems.map((item, i) => (
                  <div key={i} className="exp-apparel-item">
                    <div
                      className="exp-apparel-img"
                      style={{ backgroundImage: `url(${item.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                    />
                    <p className="exp-apparel-name">{item.name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Comments Tab View Setup */}
        {activeTab === 'comments' && (
          <div className="exp-tab-content">
            <div className="exp-comments-list">
              {comments.length === 0 && (
                <p className="exp-comments-empty">No comments yet — be the first!</p>
              )}
              {comments.map((c, i) => (
                <div key={i} className="exp-comment">
                  <div className="exp-comment-avatar">
                    <span>{(c.username || 'G').charAt(0).toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="exp-comment-user">{c.username || 'Guest'}</p>
                    <p className="exp-comment-text">{c.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="exp-comment-input-row">
              <input
                className="exp-comment-input"
                placeholder="Add a comment…"
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handlePostComment()}
              />
              <button className="exp-comment-send" onClick={handlePostComment}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* External Website Direct Target Action */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 30, marginBottom: 80 }}>
            <a 
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="profile-edit-btn" 
              style={{ 
                textDecoration: 'none', 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '8px',
                justifyContent: 'center'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18px" height="18px" viewBox="0 0 24 24" fill="none"> 
                <g id="Interface / External_Link"> 
                  <path id="Vector" d="M10.0002 5H8.2002C7.08009 5 6.51962 5 6.0918 5.21799C5.71547 5.40973 5.40973 5.71547 5.21799 6.0918C5 6.51962 5 7.08009 5 8.2002V15.8002C5 16.9203 5 17.4801 5.21799 17.9079C5.40973 18.2842 5.71547 18.5905 6.0918 18.7822C6.5192 19 7.07899 19 8.19691 19H15.8031C16.921 19 17.48 19 17.9074 18.7822C18.2837 18.5905 18.5905 18.2839 18.7822 17.9076C19 17.4802 19 16.921 19 15.8031V14M20 9V4M20 4H15M20 4L13 11" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/> 
                </g> 
              </svg>
              {ctaText}
           </a>
        </div>
      </div>
    </div>
  )
}